<script setup>
import TD from "@/Components/Table/Element.vue";
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Select from "@/Components/Select.vue";
import Label from "@/Components/Settings/Label.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import HeaderButton from "@/Components/Settings/HeaderButton.vue";
import Heading from "@/Components/Settings/Heading.vue";
import { Head, useForm } from "@inertiajs/inertia-vue3";
import VueTimepicker from "vue3-timepicker";
import { ref, onUnmounted } from "vue";
import { Inertia } from "@inertiajs/inertia";

const page_name = "Notdienst";

const isLoading = ref(false);

const props = defineProps({
    emergencyService: Object,
    interval: Number,
    errors: Object,
    practitioners: Array,
});

const form = useForm({
    practitioner_id: props.emergencyService.practitioner_id,
    tel: props.emergencyService.tel,
    date: props.emergencyService.date,
    time_start: props.emergencyService.time_start,
    time_end: props.emergencyService.time_end,
    repetition: props.emergencyService.repetition, // once, daily, weekly, monthly
    repetition_mode: props.emergencyService.repetition_mode, // always, amount, date
    repetition_value_amount: props.emergencyService.repetition_value_amount,
    repetition_value_date: props.emergencyService.repetition_value_date,
});
let saveBtnClicked = false;

//onUnmounted(
//    Inertia.on("before", (e) => {
//        if (form.isDirty && !saveBtnClicked) {
//            return confirm(
//                "Sie haben ungespeicherte Eingaben. Wollen Sie diese Seite wirklich verlassen?"
//            );
//        }
//    })
//);

function update() {
    isLoading.value = true;
    saveBtnClicked = true;

    form.put("/emergencyService/" + props.emergencyService.id, {
        onFinish: () => {
            isLoading.value = false;
        },
    });
}
</script>

<template>
    <SettingsLayout>
        <!-- <Head title="Stammdaten - Grundeinstellungen" /> -->
        <Head :title="`${page_name} - Grundeinstellungen`" />

        <template #breadcrumb>{{ page_name }}</template>
        <template #controls>
            <HeaderLink class="mr-3" href="/" type="close"
                >Schließen</HeaderLink
            >
            <HeaderButton :disabled="isLoading" @click="update()"
                >Speichern</HeaderButton
            >
        </template>
        <Heading title="Notdienst bearbeiten" />

        <p class="max-w-2xl text-sm text-oteoText">
            Den Notdienst Ihrer Praxis legen Sie hier fest. Der Notdienst wird
            in Ihrer Kalenderansicht rot markiert, so dass immer klar
            ersichtlich ist, an welchen Tagen Sie Notdienst haben. Sie können
            den Tag, die Uhrzeit, den/die Behandler/in auswählen und eine
            Telefonnummer für Notfälle hinterlegen. Zudem können Sie vorab
            Wiederholungen bei wiederkehrenden Notdiensten definieren.
        </p>
        <div class="grid grid-cols-2 max-w-3xl gap-5 mt-8">
            <div class="">
                <Label> Bitte wählen Sie eine/n Behandler/in </Label>

                <Select class="w-full" v-model="form.practitioner_id">
                    <option
                        v-for="practitioner in practitioners"
                        :key="practitioner.id"
                        :value="practitioner.id"
                    >
                        {{
                            `${practitioner.firstname} ${practitioner.lastname}`
                        }}
                    </option>
                </Select>
                <div
                    class="mt-2 text-red-600 text-sm"
                    v-if="errors.practitioner_id"
                >
                    {{ errors.practitioner_id }}
                </div>
            </div>
            <div class="">
                <Label>
                    Telefonnummer
                    <span class="font-light">(Kontakt Notdienst)</span>
                </Label>

                <Input v-model="form.tel" class="w-full" type="tel" />
                <div class="mt-2 text-red-600 text-sm" v-if="errors.tel">
                    {{ errors.tel }}
                </div>
            </div>
        </div>

        <div class="max-w-3xl grid grid-cols-2 gap-5 mt-8">
            <div class="">
                <Label> Datum </Label>

                <Input v-model="form.date" class="w-full" type="date" />
                <div class="mt-2 text-red-600 text-sm" v-if="errors.date">
                    {{ errors.date }}
                </div>
            </div>
            <div class="">
                <Label> Zeit </Label>
                <div class="grid grid-cols-12 gap-2 items-center">
                    <div class="col-span-5">
                        <!-- <Input v-model="form.time_start" class="w-full" type="time" /> -->
                        <VueTimepicker
                            class="timepicker-input"
                            v-model="form.time_start"
                            :key="form.time_start"
                            :minute-interval="props.interval"
                            format="HH:mm"
                            close-on-complete
                            fixed-dropdown-button
                        >
                            <template v-slot:dropdownButton>
                                <img :src="'/storage/icons/clock.png'" />
                            </template>
                        </VueTimepicker>
                    </div>
                    <div class="col-span-1 justify-self-center">bis</div>
                    <div class="col-span-5">
                        <!-- <Input v-model="form.time_end" class="w-full" type="time" /> -->

                        <VueTimepicker
                            class="timepicker-input"
                            v-model="form.time_end"
                            :key="form.time_end"
                            :minute-interval="props.interval"
                            format="HH:mm"
                            close-on-complete
                            fixed-dropdown-button
                        >
                            <template v-slot:dropdownButton>
                                <img :src="'/storage/icons/clock.png'" />
                            </template>
                        </VueTimepicker>
                    </div>
                    <div class="col-span-1 justify-self-center">Uhr</div>
                </div>
                <div
                    class="mt-2 text-red-600 text-sm"
                    v-if="errors.time_start || errors.time_end"
                >
                    {{ errors.time_start }}
                    <br />
                    {{ errors.time_end }}
                </div>
            </div>
        </div>

        <div class="grid grid-cols-2 max-w-3xl gap-5 mt-8">
            <div class="col-span-1">
                <Label> Wiederholung </Label>

                <Select v-model="form.repetition" class="w-full">
                    <option value="once">einmalig</option>
                    <option value="daily">täglich</option>
                    <option value="weekly">wöchentlich</option>
                    <option value="monthly">monatlich</option>
                </Select>

                <div class="mt-2 text-red-600 text-sm" v-if="errors.repetition">
                    {{ errors.repetition }}
                </div>
            </div>
        </div>

        <div
            class="grid grid-cols-2 max-w-3xl gap-5 mt-8 text-sm text-oteoText relative"
        >
            <div
                class="col-span-1"
                :class="{ 'opacity-50': form.repetition === 'once' }"
            >
                <div class="flex items-center">
                    <input
                        :disabled="form.repetition === 'once'"
                        class="mr-4"
                        type="radio"
                        v-model="form.repetition_mode"
                        value="always"
                    />
                    Kein Enddatum
                </div>
                <div class="flex items-center my-4">
                    <div class="flex items-center w-32">
                        <input
                            :disabled="form.repetition === 'once'"
                            v-model="form.repetition_mode"
                            value="amount"
                            class="mr-4"
                            type="radio"
                        />
                        Endet nach:
                    </div>
                    <Input
                        :disabled="
                            form.repetition === 'once' ||
                            form.repetition_mode !== 'amount'
                        "
                        v-model="form.repetition_value_amount"
                        min="2"
                        max="50"
                        step="1"
                        class="mr-4 w-20"
                        type="number"
                    />
                    Notdiensten
                </div>
                <div class="flex items-center">
                    <div class="flex items-center w-32">
                        <input
                            :disabled="form.repetition === 'once'"
                            v-model="form.repetition_mode"
                            value="date"
                            class="mr-4"
                            type="radio"
                        />
                        Endet am:
                    </div>
                    <Input
                        :disabled="
                            form.repetition === 'once' ||
                            form.repetition_mode !== 'date'
                        "
                        v-model="form.repetition_value_date"
                        class="flex-1"
                        type="date"
                    />
                </div>
            </div>

            <div
                class="mt-2 text-red-600 text-sm absolute -bottom-6"
                v-if="errors.repetition_value_amount"
            >
                {{ errors.repetition_value_amount }}
            </div>
            <div
                class="mt-2 text-red-600 text-sm absolute -bottom-6"
                v-if="errors.repetition_value_date"
            >
                {{ errors.repetition_value_date }}
            </div>
        </div>
    </SettingsLayout>
</template>
